'use client'
import { urlForImage } from '@/sanity/lib/utils'
import NextImage from 'next/image'
import { cx } from '../styles/stylebook'
import { useState, useEffect, useMemo } from 'react'
import type { Image } from 'sanity'

interface ImageBoxProps {
  image?: any | Image
  alt?: string
  width?: number
  height?: number
  size?: string
  classesWrapper?: string
  'data-sanity'?: string
  useOriginalRatio?: boolean
  fixedHeight?: number | string
  fixedWidth?: number | string
  placeholder?: 'blur' | 'color' | 'logo' | 'none' | 'pixelated'
  pixelatedLoading?: boolean
  fallbackAspectRatio?: number
  fill?: 'cover' | 'contain'
}

export default function ImageBox({
  image,
  alt = 'Cover image',
  width,
  height,
  size = '100vw',
  classesWrapper,
  useOriginalRatio = true,
  fixedHeight,
  fixedWidth,
  placeholder = 'pixelated',
  pixelatedLoading,
  fallbackAspectRatio = 16 / 9, // 16:9 기본 비율로 변경
  fill = 'cover',
  ...props
}: ImageBoxProps) {
  // If placeholder is 'pixelated', automatically enable pixelatedLoading
  const shouldUsePixelatedLoading =
    pixelatedLoading !== undefined ? pixelatedLoading : placeholder === 'pixelated'

  const [imageLoaded, setImageLoaded] = useState(false)
  const [blurDataUrl, setBlurDataUrl] = useState<string | null>(null)
  const [blurImageUrl, setBlurImageUrl] = useState<string | null>(null)

  // 자동 차원 감지를 위한 상태
  const [detectedDimensions, setDetectedDimensions] = useState<{
    width: number
    height: number
    aspectRatio: number
  } | null>(null)

  // Sanity LQIP 또는 blurHash 활용
  const sanityBlurDataUrl = useMemo(() => {
    return (image as any)?.asset?.metadata?.lqip
  }, [(image as any)?.asset?.metadata?.lqip])

  // 자동 차원 감지 효과
  useEffect(() => {
    // 메타데이터가 이미 있거나 이미지가 없으면 감지 불필요
    const hasMetadata = !!(image as any)?.asset?.metadata?.dimensions?.width
    if (hasMetadata || !image || detectedDimensions) return

    const urlBuilder = urlForImage(image)
    const imageUrl = urlBuilder?.auto('format').url()

    if (imageUrl) {
      const img = new window.Image()
      img.crossOrigin = 'anonymous'
      img.onload = () => {
        const aspectRatio = img.naturalWidth / img.naturalHeight
        setDetectedDimensions({
          width: img.naturalWidth,
          height: img.naturalHeight,
          aspectRatio,
        })
      }
      img.onerror = () => {
        console.warn('⚠️ 이미지 차원 자동 감지 실패')
      }
      img.src = imageUrl
    }
  }, [image, detectedDimensions])

  // Generate blur placeholder data URL - 최적화된 버전
  useEffect(() => {
    if (!shouldUsePixelatedLoading || !(image as any)?.asset?._ref) return

    // Sanity LQIP가 있으면 우선 사용
    if (sanityBlurDataUrl && !blurDataUrl) {
      setBlurDataUrl(sanityBlurDataUrl)
      return
    }

    // LQIP가 없고 아직 blur URL을 생성하지 않은 경우에만 생성
    if (!blurDataUrl && !blurImageUrl) {
      const urlBuilder = urlForImage(image)
      const lowResUrl = urlBuilder?.width(20).height(20).fit('crop').auto('format').url()

      if (lowResUrl) {
        const img = new window.Image()
        img.crossOrigin = 'anonymous'
        img.onload = () => {
          try {
            const canvas = document.createElement('canvas')
            const ctx = canvas.getContext('2d')
            if (!ctx) return

            canvas.width = 20
            canvas.height = 20

            // 더 나은 픽셀화 효과를 위한 설정
            ctx.imageSmoothingEnabled = false
            ctx.drawImage(img, 0, 0, 20, 20)

            const dataUrl = canvas.toDataURL('image/jpeg', 0.3)
            setBlurDataUrl(dataUrl)
          } catch (error) {
            // Canvas 오류 시 원본 URL 사용
            setBlurImageUrl(lowResUrl)
          }
        }
        img.onerror = () => setBlurImageUrl(lowResUrl)
        img.src = lowResUrl
      }
    }
  }, [
    shouldUsePixelatedLoading,
    (image as any)?.asset?._ref,
    sanityBlurDataUrl,
    blurDataUrl,
    blurImageUrl,
  ])

  // 이미지 메타데이터에서 원본 크기와 비율 가져오기 - 메모이제이션으로 최적화
  const { originalWidth, originalHeight, originalAspectRatio, hasValidMetadata } = useMemo(() => {
    // Sanity Image에서 asset reference 처리
    const width = (image as any)?.asset?.metadata?.dimensions?.width
    const height = (image as any)?.asset?.metadata?.dimensions?.height
    const aspectRatio = (image as any)?.asset?.metadata?.dimensions?.aspectRatio

    // 1. Sanity 메타데이터 우선 사용
    if (aspectRatio && aspectRatio > 0) {
      return {
        originalWidth: width,
        originalHeight: height,
        originalAspectRatio: aspectRatio,
        hasValidMetadata: true,
      }
    } else if (width && height && width > 0 && height > 0) {
      return {
        originalWidth: width,
        originalHeight: height,
        originalAspectRatio: width / height,
        hasValidMetadata: true,
      }
    }

    // 2. 자동 감지된 차원 사용
    if (detectedDimensions) {
      return {
        originalWidth: detectedDimensions.width,
        originalHeight: detectedDimensions.height,
        originalAspectRatio: detectedDimensions.aspectRatio,
        hasValidMetadata: true,
      }
    }

    // 3. Fallback 비율 사용
    return {
      originalWidth: width,
      originalHeight: height,
      originalAspectRatio: fallbackAspectRatio,
      hasValidMetadata: false,
    }
  }, [(image as any)?.asset?.metadata?.dimensions, fallbackAspectRatio, detectedDimensions])

  // 디스플레이 크기 계산 - 메모이제이션으로 최적화
  const { displayWidth, displayHeight, containerStyle } = useMemo(() => {
    let calcWidth: number = 800
    let calcHeight: number = 600
    let style: React.CSSProperties = {}

    if (useOriginalRatio && (fixedHeight || fixedWidth)) {
      // 고정 크기가 지정된 경우 원본 비율 유지
      if (fixedHeight) {
        if (typeof fixedHeight === 'string') {
          style.height = fixedHeight
          style.aspectRatio = originalAspectRatio.toString()
          // Next.js Image를 위한 기본 크기 (실제 렌더링은 CSS가 담당)
          calcHeight = 600
          calcWidth = Math.round(600 * originalAspectRatio)
        } else {
          calcHeight = fixedHeight
          calcWidth = Math.round(fixedHeight * originalAspectRatio)
          style.width = `${calcWidth}px`
          style.height = `${calcHeight}px`
        }
      } else if (fixedWidth) {
        if (typeof fixedWidth === 'string') {
          style.width = fixedWidth
          style.aspectRatio = originalAspectRatio.toString()
          // Next.js Image를 위한 기본 크기
          calcWidth = 800
          calcHeight = Math.round(800 / originalAspectRatio)
        } else {
          calcWidth = fixedWidth
          calcHeight = Math.round(fixedWidth / originalAspectRatio)
          style.width = `${calcWidth}px`
          style.height = `${calcHeight}px`
        }
      }
    } else if (width && height) {
      // 명시적으로 크기가 지정된 경우
      calcWidth = width
      calcHeight = height
      if (!useOriginalRatio) {
        style.width = `${width}px`
        style.height = `${height}px`
      }
    } else {
      // 자동 크기 계산
      const maxSize = 1200
      if (hasValidMetadata && originalWidth && originalHeight) {
        // 메타데이터가 있는 경우
        if (originalWidth > originalHeight) {
          calcWidth = Math.min(originalWidth, maxSize)
          calcHeight = Math.round(calcWidth / originalAspectRatio)
        } else {
          calcHeight = Math.min(originalHeight, maxSize)
          calcWidth = Math.round(calcHeight * originalAspectRatio)
        }
      } else {
        // 메타데이터가 없는 경우 fallback 비율 사용
        if (originalAspectRatio >= 1) {
          // 가로가 더 긴 경우
          calcWidth = 800
          calcHeight = Math.round(800 / originalAspectRatio)
        } else {
          // 세로가 더 긴 경우
          calcHeight = 600
          calcWidth = Math.round(600 * originalAspectRatio)
        }
      }

      if (useOriginalRatio) {
        style.aspectRatio = originalAspectRatio.toString()
      }
    }

    return {
      displayWidth: calcWidth,
      displayHeight: calcHeight,
      containerStyle: style,
    }
  }, [
    useOriginalRatio,
    fixedHeight,
    fixedWidth,
    width,
    height,
    originalAspectRatio,
    hasValidMetadata,
    originalWidth,
    originalHeight,
  ])

  // 이미지 URL 생성 - 메모이제이션으로 최적화
  const imageUrl = useMemo(() => {
    if (!image?.asset?._ref) return undefined

    const urlBuilder = urlForImage(image)
    if (!urlBuilder) return undefined

    // 최적화된 이미지 요청
    if (width && height && !useOriginalRatio) {
      // 명시적 크기 지정 + 비율 무시시에만 crop 적용
      return urlBuilder.width(displayWidth).height(displayHeight).fit('crop').auto('format').url()
    } else {
      // 대부분의 경우: 자동 포맷 + 최대 크기 제한
      const maxDimension = Math.max(displayWidth, displayHeight)
      return urlBuilder
        .width(Math.min(maxDimension * 2, 2400))
        .auto('format')
        .fit('max')
        .url()
    }
  }, [(image as any)?.asset?._ref, width, height, useOriginalRatio, displayWidth, displayHeight])

  return (
    <div
      className={cx(
        'relative overflow-hidden',
        placeholder === 'none' ? 'bg-transparent' : 'bg-gray-95',
        useOriginalRatio ? '' : 'w-full',
        classesWrapper || ''
      )}
      style={containerStyle}
      data-sanity={props['data-sanity']}
    >
      {/* Pixelated loading placeholder - 최적화된 버전 */}
      {shouldUsePixelatedLoading &&
        !imageLoaded &&
        (blurDataUrl || blurImageUrl || sanityBlurDataUrl) && (
          <img
            className="absolute inset-0 h-full w-full object-cover transition-opacity duration-300"
            alt={`${alt} - loading`}
            src={blurDataUrl || blurImageUrl || sanityBlurDataUrl!}
            loading="eager"
            style={{
              imageRendering: 'pixelated',
              filter: 'contrast(1.1) saturate(1.2) brightness(0.8)',
              opacity: imageLoaded ? 0 : 1,
              zIndex: 1,
            }}
            onLoad={() => {
              // 작은 이미지이므로 즉시 로딩 처리
            }}
          />
        )}

      {imageUrl && (
        <NextImage
          className={cx(
            'h-full w-full transition-opacity duration-500 ease-out',
            fill === 'cover' ? 'object-cover' : 'object-contain'
          )}
          alt={alt}
          width={displayWidth}
          height={displayHeight}
          sizes={size}
          src={imageUrl}
          priority={placeholder === 'none'} // 플레이스홀더가 없으면 우선 로딩
          placeholder={!shouldUsePixelatedLoading && placeholder === 'blur' ? 'blur' : 'empty'}
          blurDataURL={
            !shouldUsePixelatedLoading && placeholder === 'blur'
              ? sanityBlurDataUrl || blurDataUrl || undefined
              : undefined
          }
          style={{
            opacity: imageLoaded ? 1 : shouldUsePixelatedLoading ? 0 : 1,
            zIndex: 2,
          }}
          onLoad={() => {
            if (shouldUsePixelatedLoading) {
              // 픽셀화 효과가 있을 때만 지연
              setTimeout(() => setImageLoaded(true), 200)
            } else {
              setImageLoaded(true)
            }
          }}
          onError={() => {
            // 이미지 로딩 실패 시에도 플레이스홀더 제거
            setImageLoaded(true)
          }}
        />
      )}
    </div>
  )
}
